import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Camera, Upload, Trash2, X, Loader2, Image as ImageIcon, Plus, ChevronLeft, ChevronRight,
} from "lucide-react";
import { fileToResizedBase64, dataUrlToResizedBase64 } from "../lib/imageResize";
import { isNativeApp } from "../lib/platform";
import { haptic } from "../lib/haptics";
import { notifyBadgeUnlock } from "../lib/badges";

/* -------------------- helpers -------------------- */
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
};

/* -------------------- authenticated <img> -------------------- */
/**
 * Renders the full-res image bytes fetched from /progress-photos/{id}/image
 * via the JWT-authenticated axios client, then converts to an object URL.
 * Auto-revokes the URL on unmount to prevent memory leaks.
 */
function AuthenticatedImage({ photoId, alt = "", className = "" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoked = false;
    let url = null;
    (async () => {
      try {
        const { data } = await api.get(`/progress-photos/${photoId}/image`, { responseType: "blob" });
        url = URL.createObjectURL(data);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc(null);
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photoId]);

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-[#121212] ${className}`}>
        <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} draggable="false" />;
}

/* -------------------- main page -------------------- */
export default function ProgressPhotosPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null); // index in photos[] when set
  const [pendingCaption, setPendingCaption] = useState("");
  const [pendingWeight, setPendingWeight] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/progress-photos");
      setPhotos(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Could not load photos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ---- upload via file picker ---- */
  const onPickFile = () => fileRef.current?.click();
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-picking the same file
    await doUpload(() => fileToResizedBase64(file));
  };

  /* ---- upload via Capacitor native camera ---- */
  const onNativeCamera = async () => {
    if (!isNativeApp()) return;
    try {
      const { Camera: CapCam, CameraResultType } = await import("@capacitor/camera");
      const photo = await CapCam.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
      });
      if (!photo.dataUrl) return;
      await haptic("light");
      await doUpload(() => dataUrlToResizedBase64(photo.dataUrl));
    } catch (err) {
      // User cancelling is a common case — only show a toast for real errors.
      const msg = err?.message || "";
      if (!msg.toLowerCase().includes("cancel")) {
        toast.error("Camera unavailable");
      }
    }
  };

  const doUpload = async (sourceFn) => {
    setUploading(true);
    try {
      const { base64 } = await sourceFn();
      const body = {
        image_data: base64,
        caption: pendingCaption.trim() || null,
        weight_kg: pendingWeight ? Number(pendingWeight) : null,
      };
      const { data } = await api.post("/progress-photos", body);
      setPhotos((p) => [data, ...p]);
      setPendingCaption("");
      setPendingWeight("");
      toast.success("Photo saved");
      await haptic("success");
      notifyBadgeUnlock(data?.badge_unlocked);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ---- delete ---- */
  const removePhoto = async (id) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    try {
      await api.delete(`/progress-photos/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
      // If the lightbox was on this photo, close it.
      setLightboxIdx((i) => (i !== null && photos[i]?.id === id ? null : i));
      toast.success("Photo deleted");
    } catch {
      toast.error("Could not delete photo");
    }
  };

  /* ---- lightbox keyboard nav ---- */
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowLeft") setLightboxIdx((i) => Math.max(0, (i ?? 0) - 1));
      if (e.key === "ArrowRight") setLightboxIdx((i) => Math.min(photos.length - 1, (i ?? 0) + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, photos.length]);

  const lightboxPhoto = lightboxIdx !== null ? photos[lightboxIdx] : null;

  return (
    <div className="px-6 lg:px-10 py-10 max-w-7xl mx-auto" data-testid="progress-photos-page">
      <header className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] mb-2">Body progress</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none mb-3">
            Your transformation log.
          </h1>
          <p className="text-white/60 max-w-xl text-sm sm:text-base">
            Private photos, encrypted at rest, only visible to you. Tag each shot with weight + caption to track your changes over time.
          </p>
        </div>
      </header>

      {/* Uploader */}
      <div className="border border-white/10 bg-[#121212] p-6 mb-8" data-testid="upload-card">
        <div className="label-eyebrow text-white/50 mb-3">New photo</div>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <input
            type="text"
            value={pendingCaption}
            onChange={(e) => setPendingCaption(e.target.value)}
            placeholder="Caption (optional) — e.g. 12-week cut, week 4"
            className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="photo-caption-input"
            maxLength={120}
          />
          <input
            type="number"
            min={20}
            max={400}
            step={0.1}
            value={pendingWeight}
            onChange={(e) => setPendingWeight(e.target.value)}
            placeholder="Weight (kg, optional)"
            className="w-full bg-[#0a0a0a] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
            data-testid="photo-weight-input"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onPickFile}
            disabled={uploading}
            className="bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="upload-button"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : "Choose photo"}
          </button>
          {isNativeApp() && (
            <button
              type="button"
              onClick={onNativeCamera}
              disabled={uploading}
              className="border border-white/15 text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:border-[#6B9BFF] hover:text-[#6B9BFF] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="native-camera-button"
            >
              <Camera className="w-3.5 h-3.5" /> Use camera
            </button>
          )}
          <div className="text-xs text-white/40">Private to you · resized to 1280px JPEG</div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFileChosen}
        />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : photos.length === 0 ? (
        <div className="border border-dashed border-white/15 p-12 text-center" data-testid="empty-state">
          <ImageIcon className="w-8 h-8 text-white/30 mx-auto mb-3" />
          <div className="font-display text-xl mb-2">No photos yet.</div>
          <p className="text-white/50 text-sm mb-5">
            Upload your first progress shot above. They&apos;ll appear here in reverse-chronological order.
          </p>
          <button
            type="button"
            onClick={onPickFile}
            className="inline-flex items-center gap-2 text-[#6B9BFF] text-xs uppercase tracking-widest font-bold hover:text-white"
          >
            <Plus className="w-3.5 h-3.5" /> Add first photo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid">
          {photos.map((p, idx) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setLightboxIdx(idx)}
              className="group relative aspect-square overflow-hidden border border-white/10 hover:border-[#6B9BFF] transition-colors"
              data-testid={`photo-tile-${p.id}`}
            >
              <img
                src={`data:image/jpeg;base64,${p.thumb_b64}`}
                alt={p.caption || `Progress photo ${fmtDate(p.taken_at)}`}
                className="w-full h-full object-cover"
                draggable="false"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2.5 text-left">
                <div className="text-[10px] label-eyebrow text-white/90">{fmtDate(p.taken_at)}</div>
                {p.weight_kg && (
                  <div className="text-xs text-[#6B9BFF] font-bold mt-0.5">{p.weight_kg} kg</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col"
          data-testid="lightbox"
          onClick={() => setLightboxIdx(null)}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm text-white/70" data-testid="lightbox-meta">
              {fmtDate(lightboxPhoto.taken_at)}
              {lightboxPhoto.weight_kg && (
                <span className="ml-3 text-[#6B9BFF] font-bold">{lightboxPhoto.weight_kg} kg</span>
              )}
              {lightboxPhoto.caption && (
                <span className="ml-3 text-white/50">· {lightboxPhoto.caption}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removePhoto(lightboxPhoto.id); }}
                className="w-9 h-9 flex items-center justify-center text-white/60 hover:text-[#FF3B30] hover:bg-white/5"
                data-testid="lightbox-delete"
                aria-label="Delete photo"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
                className="w-9 h-9 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5"
                data-testid="lightbox-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center relative">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => Math.max(0, (i ?? 0) - 1)); }}
              disabled={lightboxIdx === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white disabled:opacity-20"
              data-testid="lightbox-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <AuthenticatedImage
              photoId={lightboxPhoto.id}
              alt={lightboxPhoto.caption || "Progress photo"}
              className="max-h-[80vh] max-w-[90vw] object-contain"
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => Math.min(photos.length - 1, (i ?? 0) + 1)); }}
              disabled={lightboxIdx === photos.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white disabled:opacity-20"
              data-testid="lightbox-next"
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          <div className="text-center label-eyebrow text-white/30 py-3 text-[10px]">
            {lightboxIdx + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
